import React from 'react';
// mutations handled inside hooks
import { useAnnouncementByIdQuery } from '@features/announcements/queries/announcements.queries';
import { SlideForm } from './components/slide-form';
import { CircularProgress } from '@mui/material';
import { useSite } from '@features/sites/queries/use-site.queries';
import { useOrgSites } from '@features/sites/queries/use-org-sites.queries';
import { useBulkAccess } from './hooks/use-bulk-access';
import { useSaveSlide } from './hooks/use-save-slide';
import { useAnnouncementsByBulkId } from '@features/announcements/queries/use-announcements-by-bulk-id.queries';
import { useBulkConfirmationDialog } from './modals/use-bulk-confirmation-dialog';

const steps = [
  {
    id: 'format',
    title: 'Choose the Slide Format',
    label: 'Format',
  },
  {
    id: 'description',
    title: 'Write the Slide Content',
    label: 'Description',
  },
  {
    id: 'preferences',
    title: 'Set the Display Preferences',
    label: 'Finish',
  },
];

interface AddSlideReactProps {
  onSave: () => void;
  onCancel: () => void;
  siteId: number;
  organizationId: number;
  announcementId?: number;
}

export function AddSlideReact({
  onSave,
  onCancel,
  siteId,
  organizationId,
  announcementId,
}: AddSlideReactProps) {
  // isSubmitting handled by useSaveSlide

  const { data: site, isLoading: isSiteLoading } = useSite(siteId);

  const {
    announcement: existingAnnouncement,
    isLoading: isAnnouncementLoading,
  } = useAnnouncementByIdQuery(announcementId);

  // Bulk access and org sites
  const { bulkEnabled } = useBulkAccess();
  const { data: orgSites = [] } = useOrgSites(organizationId, { enabled: !!organizationId });
  const { data: bulkGroupData = [] } = useAnnouncementsByBulkId(
    existingAnnouncement?.bulkAnnouncementId,
    { enabled: !!existingAnnouncement?.bulkAnnouncementId },
  );
  const bulkGroup = (bulkGroupData || []).filter((announcement: any) => !announcement?.deleted);
  const initialTargetSiteIds = (bulkGroup || [])
    .map((a: any) => a.siteId)
    .filter(Boolean);
  const { confirmBulkChanges, dialogElement: bulkConfirmationDialog } =
    useBulkConfirmationDialog();
  const { saveSlide, isSubmitting } = useSaveSlide({
    siteId,
    organizationId,
    orgSites,
    bulkEnabled,
    existingAnnouncement,
    bulkGroup,
    confirmBulkChanges,
  });

  // Extra logging so we can see bulk UI eligibility at runtime
  // eslint-disable-next-line no-console
  console.log('[AddSlide] bulkEnabled', bulkEnabled, {
    orgSites: Array.isArray(orgSites) ? orgSites.length : 0,
    siteId,
    organizationId,
  });

  if ((announcementId && isAnnouncementLoading) || isSiteLoading || !site) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      {bulkConfirmationDialog}
      <SlideForm
        onSave={async (form) => {
          await saveSlide(form);
          onSave();
        }}
        onCancel={onCancel}
        organizationId={organizationId}
        existingAnnouncement={existingAnnouncement}
        steps={steps}
        isSubmitting={isSubmitting}
        site={site}
        bulkEnabled={bulkEnabled}
        initialTargetSiteIds={initialTargetSiteIds}
      />
    </>
  );
}
